import React from "react";
import { useWM } from "./store";
import { useAuth } from "./AuthContext";
import { APPS } from "./apps";

// Apps shown as quick-launch shortcuts on the taskbar (after the HEXA start button).
const TASKBAR_APP_IDS = ["dashboard", "chat", "mail", "browser", "files", "calendar", "dayrecap"];

// Color tint per app (used by the start menu too) — duplicated here to keep this
// component self-contained and avoid circular imports.
const TASKBAR_TINTS = {
  chat: "#6366f1",
  mail: "#3b82f6",
  browser: "#14b8a6",
  files: "#fbbf24",
  calendar: "#0ea5e9",
  dayrecap: "#22d3ee",
  dashboard: "#7c3aed",
};

/**
 * Persistent bottom-center taskbar (Win11-style). Always visible.
 * - HEXA hex (start) button: opens the Start Menu hero overlay. When apps are
 *   already open, it minimises them so the hero is uncovered and the menu shows.
 * - Quick-launch app icons: open / focus their app.
 */
export default function Taskbar() {
  const { open, windows, minimizeAll, restoreAll, focus } = useWM();
  const { appAccess } = useAuth();
  const hasVisibleWindow = windows.some((w) => !w.isMinimized);
  const [hover, setHover] = React.useState(false);
  // When any sub-app is visible, collapse the taskbar off-screen and reveal
  // it on hover of the bottom edge. Also lets the user surface it via the
  // Windows key.
  const collapsed = hasVisibleWindow && !hover;

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Meta" || e.key === "OS") {
        setHover((v) => !v);
      }
    };
    // The top bar's TASKBAR button was calling an onToggleTaskbar prop that
    // Desktop never passed, so clicking it did nothing. Visibility lives here,
    // so let the button reach it by event instead of lifting state.
    const onToggle = () => setHover((v) => !v);
    window.addEventListener("keydown", onKey);
    window.addEventListener("hexa:toggle-taskbar", onToggle);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("hexa:toggle-taskbar", onToggle);
    };
  }, []);

  const onStartClick = () => {
    const anyVisible = windows.some((w) => !w.isMinimized);
    if (anyVisible) {
      // Hide every open app so the HEXA Start hero (and its apps grid) is
      // visible. The flag below is read by HeroLanding when it mounts.
      window.__hexaShowApps = true;
      minimizeAll();
      window.dispatchEvent(new CustomEvent("hexa:open-apps"));
    } else if (windows.length > 0) {
      // All currently minimized — toggle: bring them back into view.
      restoreAll();
    } else {
      // No windows at all — just toggle the apps grid on the hero.
      window.dispatchEvent(new CustomEvent("hexa:toggle-apps"));
    }
  };

  return (
    <>
      {/* Bottom hover-trigger strip — always mounted. When the taskbar is
          collapsed (a sub-app is visible), mouse-entering this strip brings
          the taskbar back. When the taskbar is already visible, the strip is
          harmless. */}
      <div
        data-testid="taskbar-hover-zone"
        onMouseEnter={() => setHover(true)}
        className="fixed bottom-0 left-0 right-0 z-[399]"
        style={{ height: 12, pointerEvents: collapsed ? "auto" : "none" }}
      />
      <div
        data-testid="taskbar"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="fixed left-1/2 z-[400] pointer-events-auto"
        style={{
          bottom: collapsed ? -72 : 12,
          transform: "translate(-50%, 0)",
          transition: "bottom 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease",
          opacity: collapsed ? 0 : 1,
        }}
      >
      <div
        className="flex items-center gap-1 px-2 py-1.5 rounded-2xl"
        style={{
          background: "rgba(12, 18, 36, 0.8)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          border: "1px solid rgba(139,92,246,0.22)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.55)",
        }}
      >
        <button
          data-testid="taskbar-start"
          title="HEXA"
          onClick={onStartClick}
          className="grid place-items-center w-10 h-10 rounded-xl hover:bg-white/8 transition"
        >
          <img
            src="/hexa-icon-tight.png"
            alt="HEXA"
            style={{
              width: 26,
              height: 26,
              objectFit: "contain",
            }}
          />
        </button>

        {TASKBAR_APP_IDS.filter((id) => !appAccess?.hidden?.includes(id)).map((id) => {
          const app = APPS.find((a) => a.id === id);
          if (!app) return null;
          const Icon = app.icon;
          const tint = TASKBAR_TINTS[id] || "#8b5cf6";
          const openWin = windows.find((w) => w.appId === id);
          const isActive = !!openWin;
          return (
            <button
              key={app.id}
              data-testid={`taskbar-${app.id}`}
              onClick={() => {
                if (openWin) focus(openWin.id);
                else open(app);
              }}
              title={app.title}
              className={`grid place-items-center w-10 h-10 rounded-xl transition ${isActive ? "bg-white/12" : "hover:bg-white/8"}`}
            >
              <Icon size={22} strokeWidth={1.8} color={tint} />
              {isActive && (
                <span
                  aria-hidden
                  className="absolute"
                  style={{
                    bottom: 2,
                    width: 14,
                    height: 2,
                    borderRadius: 2,
                    background: tint,
                    boxShadow: `0 0 8px ${tint}`,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
    </>
  );
}
